/**
 * Tab Navigation System - Persistence Layer
 *
 * Handles saving and restoring tab state to/from localStorage.
 * 
 * Multi-window support:
 * - Each browser window gets a unique windowId stored in sessionStorage
 * - Pinned tabs are shared (synced to backend)
 * - Recent tabs, active tab, and histories are window-local (localStorage per windowId)
 */

import { STORAGE_KEYS, TABS_SCHEMA_VERSION } from '@/config/tabsConfig';
import type { PersistedTabState, SplitOrientation, TabHistoryEntry, TabItem, TabPane, TabsConfig } from './types';

/** Best-effort parse of the shared preferences localStorage string; `{}` if unusable. */
export function parseSharedPreferencesObject(raw: string | null): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // corrupt JSON
  }
  return {};
}

// =============================================================================
// Window ID Management (for multi-window support)
// =============================================================================

const WINDOW_ID_KEY = 'utx-window-id';
const WINDOW_LOCAL_TABS_KEY_PREFIX = 'utx-tabs-window';

/**
 * Get the unique ID for this browser window/tab.
 * Creates one if it doesn't exist (persists in sessionStorage for this window's lifetime).
 */
export function getWindowId(): string {
  let windowId = sessionStorage.getItem(WINDOW_ID_KEY);
  if (!windowId) {
    windowId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(WINDOW_ID_KEY, windowId);
  }
  return windowId;
}

/**
 * Get namespaced localStorage key for window-local data.
 */
export function getWindowLocalStorageKey(base: string): string {
  return `${base}-${getWindowId()}`;
}

/**
 * Normalize path to handle redirect routes
 * Converts root path '/' to '/dashboard' since '/' is a redirect route with no component
 */
export function normalizePath(path: string): string {
  return path === '/' ? '/dashboard' : path;
}

/**
 * Extract the base path (pathname only) without search params
 * e.g., '/users/123?tab=invoices' -> '/users/123'
 */
export function getBasePath(path: string): string {
  const questionMarkIndex = path.indexOf('?');
  return questionMarkIndex !== -1 ? path.slice(0, questionMarkIndex) : path;
}

/**
 * Extract search params from a path
 * e.g., '/users/123?tab=invoices' -> '?tab=invoices'
 */
export function getSearchFromPath(path: string): string {
  const questionMarkIndex = path.indexOf('?');
  return questionMarkIndex !== -1 ? path.slice(questionMarkIndex) : '';
}

/**
 * Generate a unique key for a tab based on path
 * Uses crypto.randomUUID for collision-free keys
 */
export function generateTabKey(path: string): string {
  // Use crypto.randomUUID if available (modern browsers), otherwise fallback to timestamp + random
  const uniqueId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  return `${path}-${uniqueId}`;
}

/**
 * Create a new tab item with initial history
 */
export function createTabItem(
  path: string,
  title: string,
  options?: {
    icon?: string;
    search?: string;
    state?: unknown;
    pinned?: boolean;
    pinnedOrder?: number;
  }
): TabItem {
  const now = Date.now();
  const initialEntry: TabHistoryEntry = {
    path,
    search: options?.search || '',
    state: options?.state,
    timestamp: now,
  };

  return {
    key: generateTabKey(path),
    path,
    title,
    icon: options?.icon,
    closable: !options?.pinned,
    pinned: options?.pinned || false,
    pinnedOrder: options?.pinnedOrder,
    history: [initialEntry],
    historyIndex: 0,
    lastAccessed: now,
  };
}

/**
 * Save tabs state to localStorage
 */
export function persistTabsState(
  tabs: TabItem[],
  activeTabKey: string | null,
  config: TabsConfig
): void {
  if (!config.persistPinnedTabs && !config.persistRecentTabs) {
    return;
  }

  try {
    const pinnedTabs = tabs
      .filter((tab) => tab.pinned)
      .map((tab) => ({
        path: normalizePath(getBasePath(tab.path)),
        title: tab.title,
        icon: tab.icon,
        pinnedOrder: tab.pinnedOrder,
        lastAccessed: tab.lastAccessed,
        historyIndex: tab.historyIndex,
      }));

    // Preserve original tab order by using index, but limit by lastAccessed for max tabs
    const unpinnedTabs = tabs.filter((tab) => !tab.pinned);
    const tabsToKeep = config.persistRecentTabs
      ? unpinnedTabs.length <= config.maxRestoredTabs
        ? unpinnedTabs
        : // If over limit, keep most recently accessed but preserve their original order
          unpinnedTabs
            .map((tab, originalIndex) => ({ tab, originalIndex }))
            .sort((a, b) => b.tab.lastAccessed - a.tab.lastAccessed)
            .slice(0, config.maxRestoredTabs)
            .sort((a, b) => a.originalIndex - b.originalIndex)
            .map(({ tab }) => tab)
      : [];

    const recentTabs = tabsToKeep.map((tab, index) => ({
      path: normalizePath(getBasePath(tab.path)),
      title: tab.title,
      icon: tab.icon,
      lastAccessed: tab.lastAccessed,
      historyIndex: tab.historyIndex,
      order: index, // Preserve original order
    }));

    // Persist tab histories for back/forward support
    // Use indexed keys when multiple tabs share the same base path to prevent collision
    const tabHistories: Record<string, TabHistoryEntry[]> = {};
    const pathCounts: Record<string, number> = {};
    for (const tab of tabs) {
      const baseTabPath = normalizePath(getBasePath(tab.path));
      if (tab.pinned || recentTabs.some((rt) => rt.path === baseTabPath)) {
        const count = pathCounts[baseTabPath] || 0;
        pathCounts[baseTabPath] = count + 1;
        const historyKey = count === 0 ? baseTabPath : `${baseTabPath}::${count}`;
        tabHistories[historyKey] = tab.history;
      }
    }

    // Store the active tab's PATH instead of key, since keys are regenerated on restore
    const activeTab = tabs.find((t) => t.key === activeTabKey);
    const activeTabPath = activeTab ? normalizePath(getBasePath(activeTab.path)) : null;

    // Store the active tab's index among all persisted tabs for disambiguation
    // when multiple tabs share the same path
    let activeTabIndex = -1;
    if (activeTab) {
      const activeTabOriginalIdx = tabs.indexOf(activeTab);
      const pinnedOriginalIndices = tabs
        .map((t, i) => t.pinned ? i : -1)
        .filter((i) => i >= 0);
      const recentOriginalIndices = tabsToKeep.map((t) => tabs.indexOf(t));

      const pinnedPos = pinnedOriginalIndices.indexOf(activeTabOriginalIdx);
      if (pinnedPos >= 0) {
        activeTabIndex = pinnedPos;
      } else {
        const recentPos = recentOriginalIndices.indexOf(activeTabOriginalIdx);
        if (recentPos >= 0) {
          activeTabIndex = pinnedTabs.length + recentPos;
        }
      }
    }

    const state: PersistedTabState = {
      version: TABS_SCHEMA_VERSION,
      pinnedTabs,
      recentTabs,
      activeTabKey: activeTabPath, // Actually storing path, not key
      tabHistories,
    };

    const cur = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
    const base = parseSharedPreferencesObject(cur);
    localStorage.setItem(
      STORAGE_KEYS.TABS_STATE,
      JSON.stringify({
        ...base,
        pinnedTabs: state.pinnedTabs,
        recentTabs: state.recentTabs,
        tabHistories: state.tabHistories,
        activeTabPath: state.activeTabKey,
        activeTabIndex: activeTabIndex >= 0 ? activeTabIndex : undefined,
      })
    );
  } catch (error) {
    console.error('Failed to persist tabs state:', error);
  }
}

/**
 * Restore tabs state from localStorage
 */
export function restoreTabsState(config: TabsConfig): {
  tabs: TabItem[];
  activeTabKey: string | null;
} | null {
  try {
    const leg = localStorage.getItem(STORAGE_KEYS.LEGACY_TABS_STATE);
    if (leg) {
      try {
        const rawLegacy = JSON.parse(leg) as unknown;
        if (rawLegacy === null || typeof rawLegacy !== 'object' || Array.isArray(rawLegacy)) {
          // Not a plain object: unusable; drop the key
          localStorage.removeItem(STORAGE_KEYS.LEGACY_TABS_STATE);
        } else {
          const l = rawLegacy as Record<string, unknown>;
          if (l.version !== undefined && l.version !== TABS_SCHEMA_VERSION) {
            localStorage.removeItem(STORAGE_KEYS.LEGACY_TABS_STATE);
          } else {
            try {
              const t = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
              const base = parseSharedPreferencesObject(t);
              if (Array.isArray(l.pinnedTabs)) {
                base.pinnedTabs = l.pinnedTabs;
              }
              if (Array.isArray(l.recentTabs)) {
                base.recentTabs = l.recentTabs;
              }
              if (l.tabHistories && typeof l.tabHistories === 'object' && l.tabHistories) {
                base.tabHistories = l.tabHistories;
              }
              if (l.activeTabKey !== undefined) {
                base.activeTabPath = l.activeTabKey;
              }
              localStorage.setItem(STORAGE_KEYS.TABS_STATE, JSON.stringify(base));
              // Remove legacy only after a successful write (e.g. quota errors keep legacy for retry)
              localStorage.removeItem(STORAGE_KEYS.LEGACY_TABS_STATE);
            } catch (err) {
              console.error('Failed to migrate legacy tabs to shared preferences:', err);
            }
          }
        }
      } catch {
        // Corrupt JSON in the legacy key only; safe to remove
        localStorage.removeItem(STORAGE_KEYS.LEGACY_TABS_STATE);
      }
    }

    const raw = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
    if (!raw) {
      return null;
    }
    const blob = parseSharedPreferencesObject(raw);

    const activePathFromPrefs =
      (blob.activeTabPath as string | null | undefined) ??
      (blob.activeTabKey as string | null | undefined) ??
      null;

    const state: PersistedTabState = {
      version: TABS_SCHEMA_VERSION,
      pinnedTabs: (blob.pinnedTabs as PersistedTabState['pinnedTabs']) ?? [],
      recentTabs: (blob.recentTabs as PersistedTabState['recentTabs']) ?? [],
      activeTabKey: activePathFromPrefs,
      tabHistories: (blob.tabHistories as PersistedTabState['tabHistories']) ?? {}
    };

    const tabs: TabItem[] = [];

    // Helper to normalize history entries
    const normalizeHistory = (history: TabHistoryEntry[]): TabHistoryEntry[] => {
      return history.map((entry) => ({
        ...entry,
        path: normalizePath(entry.path),
      }));
    };

    // Track how many times each path has been consumed for indexed history lookup
    const restoredPathCounts: Record<string, number> = {};

    // Restore pinned tabs
    if (config.persistPinnedTabs && state.pinnedTabs) {
      for (const pt of state.pinnedTabs) {
        const normalizedPath = normalizePath(pt.path);
        const count = restoredPathCounts[normalizedPath] || 0;
        restoredPathCounts[normalizedPath] = count + 1;
        const indexedKey = count === 0 ? normalizedPath : `${normalizedPath}::${count}`;
        const rawHistory = state.tabHistories?.[indexedKey] || state.tabHistories?.[normalizedPath] || state.tabHistories?.[pt.path] || [
          { path: normalizedPath, search: '', timestamp: pt.lastAccessed },
        ];
        const history = normalizeHistory(rawHistory);
        tabs.push({
          key: generateTabKey(normalizedPath),
          path: normalizedPath,
          title: pt.title,
          icon: pt.icon,
          closable: false,
          pinned: true,
          pinnedOrder: pt.pinnedOrder,
          history,
          historyIndex: Math.min(pt.historyIndex, history.length - 1),
          lastAccessed: pt.lastAccessed,
        });
      }
    }

    // Restore recent tabs
    // Track which pinned paths have already been "consumed" for dedup purposes
    const pinnedPathBudget: Record<string, number> = {};
    for (const t of tabs) {
      if (t.pinned) {
        pinnedPathBudget[t.path] = (pinnedPathBudget[t.path] || 0) + 1;
      }
    }

    if (config.persistRecentTabs && state.recentTabs) {
      for (const rt of state.recentTabs.slice(0, config.maxRestoredTabs)) {
        const normalizedPath = normalizePath(rt.path);
        // Skip only if this path was already restored as a pinned tab (consume one budget)
        if (pinnedPathBudget[normalizedPath] && pinnedPathBudget[normalizedPath] > 0) {
          pinnedPathBudget[normalizedPath]--;
          continue;
        }
        const count = restoredPathCounts[normalizedPath] || 0;
        restoredPathCounts[normalizedPath] = count + 1;
        const indexedKey = count === 0 ? normalizedPath : `${normalizedPath}::${count}`;
        const rawHistory = state.tabHistories?.[indexedKey] || state.tabHistories?.[normalizedPath] || state.tabHistories?.[rt.path] || [
          { path: normalizedPath, search: '', timestamp: rt.lastAccessed },
        ];
        const history = normalizeHistory(rawHistory);
        tabs.push({
          key: generateTabKey(normalizedPath),
          path: normalizedPath,
          title: rt.title,
          icon: rt.icon,
          closable: true,
          pinned: false,
          history,
          historyIndex: Math.min(rt.historyIndex, history.length - 1),
          lastAccessed: rt.lastAccessed,
        });
      }
    }

    // Find the active tab by path (activeTabKey actually contains the path since keys are regenerated)
    const activeTabPath = state.activeTabKey ? normalizePath(state.activeTabKey) : null;
    // Use stored index for disambiguation when multiple tabs share the same path
    const activeTabIdx = typeof (blob as Record<string, unknown>).activeTabIndex === 'number'
      ? (blob as Record<string, unknown>).activeTabIndex as number
      : -1;
    let activeTab: TabItem | undefined;
    if (activeTabIdx >= 0 && activeTabIdx < tabs.length) {
      activeTab = tabs[activeTabIdx];
    }
    if (!activeTab && activeTabPath) {
      activeTab = tabs.find((t) => t.path === activeTabPath);
    }
    const activeTabKey = activeTab?.key || tabs[0]?.key || null;

    return { tabs, activeTabKey };
  } catch (error) {
    console.error('Failed to restore tabs state:', error);
    return null;
  }
}

/**
 * Clear tab-related fields in the shared preferences blob.
 * Unparseable JSON is treated as `{}` so we still write cleared tab fields (self-heal), rather than no-oping.
 */
export function clearPersistedTabsState(): void {
  const t = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
  if (!t) {
    return;
  }
  const base = parseSharedPreferencesObject(t);
  try {
    localStorage.setItem(
      STORAGE_KEYS.TABS_STATE,
      JSON.stringify({ ...base, pinnedTabs: [], recentTabs: [], tabHistories: {}, activeTabPath: null })
    );
  } catch (error) {
    console.error('Failed to clear persisted tab state:', error);
  }
}

// =============================================================================
// Window-Local Tab State (Phase 2: Multi-window support)
// =============================================================================

type WindowLocalTabState = {
  version: number;
  recentTabs: Array<{
    path: string;
    title: string;
    icon?: string;
    lastAccessed: number;
    historyIndex: number;
    order?: number;
  }>;
  activeTabPath: string | null;
  activeTabIndex?: number;
  tabHistories: Record<string, TabHistoryEntry[]>;
  panes?: Array<{
    id: string;
    tabIndexes: number[];
    activeTabIndex: number | null;
    tabPaths?: string[];
    activeTabPath?: string | null;
    paneTabs?: Array<{
      path: string;
      title: string;
      icon?: string;
      pinned: boolean;
      lastAccessed: number;
      historyIndex: number;
    }>;
  }>;
  activePaneId?: string;
  splitOrientation?: SplitOrientation;
};

/**
 * Save window-local tab state (recent tabs, active tab, histories).
 * This data is specific to this browser window and not synced to backend.
 */
export function persistWindowLocalState(
  tabs: TabItem[],
  activeTabKey: string | null,
  config: TabsConfig,
  paneState?: {
    panes: TabPane[];
    activePaneId: string;
    splitOrientation: SplitOrientation;
  }
): void {
  if (!config.persistRecentTabs) {
    return;
  }

  try {
    const unpinnedTabs = tabs.filter((tab) => !tab.pinned);
    const tabsToKeep = unpinnedTabs.length <= config.maxRestoredTabs
      ? unpinnedTabs
      : unpinnedTabs
          .map((tab, originalIndex) => ({ tab, originalIndex }))
          .sort((a, b) => b.tab.lastAccessed - a.tab.lastAccessed)
          .slice(0, config.maxRestoredTabs)
          .sort((a, b) => a.originalIndex - b.originalIndex)
          .map(({ tab }) => tab);

    const recentTabs = tabsToKeep.map((tab, index) => ({
      path: normalizePath(getBasePath(tab.path)),
      title: tab.title,
      icon: tab.icon,
      lastAccessed: tab.lastAccessed,
      historyIndex: tab.historyIndex,
      order: index,
    }));

    // Persist tab histories by restored tab index so duplicate paths do not overwrite each other.
    const tabHistories: Record<string, TabHistoryEntry[]> = {};
    for (const [index, tab] of tabsToKeep.entries()) {
      const normalizedPath = normalizePath(getBasePath(tab.path));
      tabHistories[`${normalizedPath}::${index}`] = tab.history;
    }

    const activeTab = tabs.find((t) => t.key === activeTabKey);
    const activeTabPath = activeTab ? normalizePath(getBasePath(activeTab.path)) : null;
    const activeTabIndex = activeTab ? tabsToKeep.findIndex((tab) => tab.key === activeTab.key) : -1;
    const persistedTabIndexByKey = new Map(tabsToKeep.map((tab, index) => [tab.key, index]));

    const panes = paneState?.panes
      .map((pane) => {
        const tabIndexes = pane.tabKeys
          .map((tabKey) => persistedTabIndexByKey.get(tabKey))
          .filter((index): index is number => typeof index === 'number');
        const paneActiveTabIndex = pane.activeTabKey
          ? persistedTabIndexByKey.get(pane.activeTabKey) ?? null
          : null;
        return {
          id: pane.id,
          tabIndexes,
          activeTabIndex: paneActiveTabIndex,
          tabPaths: pane.tabKeys
            .map((tabKey) => tabs.find((tab) => tab.key === tabKey))
            .filter((tab): tab is TabItem => Boolean(tab))
            .map((tab) => normalizePath(getBasePath(tab.path))),
          paneTabs: pane.tabKeys
            .map((tabKey) => tabs.find((tab) => tab.key === tabKey))
            .filter((tab): tab is TabItem => Boolean(tab))
            .map((tab) => ({
              path: normalizePath(getBasePath(tab.path)),
              title: tab.title,
              icon: tab.icon,
              pinned: tab.pinned,
              lastAccessed: tab.lastAccessed,
              historyIndex: tab.historyIndex,
            })),
          activeTabPath: pane.activeTabKey && tabs.find((tab) => tab.key === pane.activeTabKey)
            ? normalizePath(getBasePath(tabs.find((tab) => tab.key === pane.activeTabKey)?.path || ''))
            : null,
        };
      })
      .filter((pane) => pane.tabIndexes.length > 0 || (pane.tabPaths?.length || 0) > 0);

    const state: WindowLocalTabState = {
      version: TABS_SCHEMA_VERSION,
      recentTabs,
      activeTabPath,
      activeTabIndex: activeTabIndex >= 0 ? activeTabIndex : undefined,
      tabHistories,
      panes,
      activePaneId: paneState?.activePaneId,
      splitOrientation: paneState?.splitOrientation,
    };

    const storageKey = getWindowLocalStorageKey(WINDOW_LOCAL_TABS_KEY_PREFIX);
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to persist window-local tabs state:', error);
  }
}

/**
 * Restore window-local tab state.
 */
export function restoreWindowLocalState(config: TabsConfig): {
  tabs: TabItem[];
  activeTabKey: string | null;
  panes?: TabPane[];
  panePaths?: Array<{
    id: string;
    tabPaths: string[];
    activeTabPath: string | null;
  }>;
  activePaneId?: string;
  splitOrientation?: SplitOrientation;
} | null {
  if (!config.persistRecentTabs) {
    return null;
  }

  try {
    const storageKey = getWindowLocalStorageKey(WINDOW_LOCAL_TABS_KEY_PREFIX);
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }

    const state: WindowLocalTabState = JSON.parse(stored);

    if (state.version !== TABS_SCHEMA_VERSION) {
      console.warn('Window-local tabs state version mismatch, clearing state');
      localStorage.removeItem(storageKey);
      return null;
    }

    const tabs: TabItem[] = [];

    const normalizeHistory = (history: TabHistoryEntry[]): TabHistoryEntry[] => {
      return history.map((entry) => ({
        ...entry,
        path: normalizePath(entry.path),
      }));
    };

    for (const rt of state.recentTabs.slice(0, config.maxRestoredTabs)) {
      const normalizedPath = normalizePath(rt.path);
      const tabIndex = tabs.length;
      const rawHistory = state.tabHistories?.[`${normalizedPath}::${tabIndex}`]
        || state.tabHistories?.[normalizedPath]
        || [
        { path: normalizedPath, search: '', timestamp: rt.lastAccessed },
      ];
      const history = normalizeHistory(rawHistory);
      tabs.push({
        key: generateTabKey(normalizedPath),
        path: normalizedPath,
        title: rt.title,
        icon: rt.icon,
        closable: true,
        pinned: false,
        history,
        historyIndex: Math.min(rt.historyIndex, history.length - 1),
        lastAccessed: rt.lastAccessed,
      });
    }

    for (const pane of state.panes || []) {
      for (const paneTab of pane.paneTabs || []) {
        const normalizedPath = normalizePath(paneTab.path);
        if (tabs.some((tab) => tab.path === normalizedPath)) {
          continue;
        }
        const restoredTab = createTabItem(normalizedPath, paneTab.title, {
          icon: paneTab.icon,
          pinned: paneTab.pinned,
        });
        tabs.push({
          ...restoredTab,
          historyIndex: Math.min(paneTab.historyIndex, restoredTab.history.length - 1),
          lastAccessed: paneTab.lastAccessed,
        });
      }
    }

    const activeTabPath = state.activeTabPath ? normalizePath(state.activeTabPath) : null;
    const activeTab = typeof state.activeTabIndex === 'number' && tabs[state.activeTabIndex]
      ? tabs[state.activeTabIndex]
      : activeTabPath ? tabs.find((t) => t.path === activeTabPath) : null;
    const activeTabKey = activeTab?.key || tabs[0]?.key || null;

    const panes = state.panes
      ?.map((pane) => {
        const tabKeys = pane.tabIndexes
          .map((index) => tabs[index]?.key)
          .filter((key): key is string => Boolean(key));
        const activePaneTabKey = typeof pane.activeTabIndex === 'number'
          ? tabs[pane.activeTabIndex]?.key || tabKeys[0] || null
          : tabKeys[0] || null;
        return {
          id: pane.id,
          tabKeys,
          activeTabKey: activePaneTabKey,
        };
      })
      .filter((pane) => pane.tabKeys.length > 0);
    const panePaths = state.panes
      ?.map((pane) => ({
        id: pane.id,
        tabPaths: (pane.tabPaths || [])
          .map((path) => normalizePath(path))
          .filter(Boolean),
        activeTabPath: pane.activeTabPath ? normalizePath(pane.activeTabPath) : null,
      }))
      .filter((pane) => pane.tabPaths.length > 0);

    return {
      tabs,
      activeTabKey,
      panes,
      panePaths,
      activePaneId: state.activePaneId,
      splitOrientation: state.splitOrientation,
    };
  } catch (error) {
    console.error('Failed to restore window-local tabs state:', error);
    return null;
  }
}

/**
 * Clear window-local tabs state for this window.
 */
export function clearWindowLocalState(): void {
  const storageKey = getWindowLocalStorageKey(WINDOW_LOCAL_TABS_KEY_PREFIX);
  localStorage.removeItem(storageKey);
}

/**
 * Check if a path matches an excluded route pattern
 */
export function isExcludedRoute(path: string, excludeRoutes: string[]): boolean {
  return excludeRoutes.some((pattern) => {
    if (pattern.endsWith('*')) {
      return path.startsWith(pattern.slice(0, -1));
    }
    return path === pattern;
  });
}

/**
 * Find an existing tab by path (for reuse logic)
 * Compares base paths only - ignores search params to avoid duplicate tabs
 * for the same resource with different query params
 */
export function findTabByPath(tabs: TabItem[], path: string): TabItem | undefined {
  const targetBasePath = getBasePath(path);

  // Check if any tab's base path matches (ignoring search params)
  const matchByBasePath = tabs.find((t) => getBasePath(t.path) === targetBasePath);
  if (matchByBasePath) return matchByBasePath;

  // Check if path matches any tab's current history entry (by base path)
  return tabs.find((t) => {
    const currentEntry = t.history[t.historyIndex];
    return currentEntry && getBasePath(currentEntry.path) === targetBasePath;
  });
}
